"use client"

import { motion } from "framer-motion"
import { MapPin, Maximize, Building2, ArrowLeft, Phone } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

const properties = [
  {
    id: 1,
    title: "مشروع 11J - الحي الثاني",
    location: "الحي الثاني",
    description: "وحدات سكنية فاخرة بتصميمات عصرية وتشطيبات عالية الجودة في موقع متميز بالحي الثاني",
    features: ["تشطيب سوبر لوكس", "موقع متميز", "قريب من الخدمات", "مساحات متنوعة"],
    images: Array.from({ length: 14 }, (_, i) => `/properties/property-1/img-${i + 1}.jpg`),
    type: "شقق سكنية",
  },
  {
    id: 2,
    title: "مشروع 44-A - الحي الخامس",
    location: "الحي الخامس",
    description: "مشروع سكني متكامل بتصميم 3D مميز يجمع بين الحداثة والراحة مع مخططات أرضية مدروسة",
    features: ["تصميم 3D حديث", "مخططات مدروسة", "إطلالات رائعة", "مرافق متكاملة"],
    images: Array.from({ length: 10 }, (_, i) => `/properties/property-2/img-${i + 1}.jpg`),
    type: "مشروع سكني",
  },
  {
    id: 3,
    title: "مشروع 82J - الحي الثاني",
    location: "الحي الثاني",
    description: "وحدات سكنية راقية بمواصفات عالية وتصميمات معمارية متميزة في قلب الحي الثاني",
    features: ["تشطيب فاخر", "مساحات واسعة", "حدائق خاصة", "أمن وحراسة"],
    images: Array.from({ length: 12 }, (_, i) => `/properties/property-3/img-${i + 1}.jpg`),
    type: "شقق وفيلات",
  },
]

function PropertyCard({ property }: { property: typeof properties[0] }) {
  const [currentImage, setCurrentImage] = useState(0)
  const displayImages = property.images.slice(0, 5)

  return (
    <Card className="group overflow-hidden border-zinc-800/50 bg-zinc-900/50 hover:border-brand/30 transition-all duration-500 rounded-2xl">
      <div className="relative aspect-[16/10] overflow-hidden">
        <Image
          src={displayImages[currentImage]}
          alt={property.title}
          fill
          className="object-cover transition-transform duration-700 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent" />
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-brand/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
          {property.type}
        </div>
        <div className="absolute bottom-4 right-4 left-4 flex gap-2">
          {displayImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentImage(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentImage ? "bg-brand flex-[2]" : "bg-white/40 flex-1 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-brand" />
          <span className="text-sm text-brand">{property.location}</span>
        </div>
        <h3 className="font-heading text-xl font-semibold text-zinc-100 mb-2">{property.title}</h3>
        <p className="text-zinc-500 text-sm mb-4 leading-relaxed">{property.description}</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {property.features.map((feature) => (
            <span
              key={feature}
              className="px-3 py-1 text-xs text-zinc-400 bg-zinc-800/80 border border-zinc-700/50 rounded-full"
            >
              {feature}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Link
            href="https://wa.me/201212129070"
            target="_blank"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-brand hover:bg-brand-dark text-white font-medium transition-colors text-sm"
          >
            <Phone className="w-4 h-4" />
            <span>استفسر الآن</span>
          </Link>
          <Link
            href="tel:+201212129070"
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-zinc-700/50 hover:border-brand/50 text-zinc-300 hover:text-brand-light transition-all text-sm"
          >
            <Phone className="w-4 h-4" />
            <span>اتصل</span>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

export function PropertiesSection() {
  return (
    <section id="properties" className="px-6 py-24 bg-zinc-900/30">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-medium text-brand uppercase tracking-wider mb-4">عقاراتنا</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-zinc-100 mb-4">
            اكتشف مشاريعنا العقارية
          </h2>
          <p className="text-zinc-500 max-w-xl mx-auto text-balance">
            مجموعة متميزة من المشاريع السكنية بتصميمات حديثة ومواقع استراتيجية
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property, index) => (
            <motion.div
              key={property.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <PropertyCard property={property} />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            href="/units"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-brand text-brand hover:bg-brand hover:text-white font-medium transition-all"
          >
            <Building2 className="w-5 h-5" />
            <span>الوحدات المتاحة</span>
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <Link
            href="https://wa.me/201212129070"
            target="_blank"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand hover:bg-brand-dark text-white font-medium transition-all"
          >
            <Building2 className="w-5 h-5" />
            <span>تواصل معنا</span>
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
